import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { reportPageView } from '~/domains/analytics/events';
import useContestInvites from '~/domains/contest/hooks/useContestInvites';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import isWebView from '~/utils/isWebView';

import { ContestInvitesStatusFilter } from '../../services/enums/contest-invites-status-filter.enum';
import { ContestStatusFilter } from '../../services/enums/contest-status-filter.enum';

import { ContestTab } from './useEntriesTabsProps';
import useEntries from './useEntries';
import useShowEliminatedContests from './useShowEliminatedContests';

const useEntriesPageData = () => {
  const router = useRouter();
  const currentTab = (router.query.contestTab as ContestTab) || ContestTab.ACTIVE;

  const getContestStatusFilter = (tab: ContestTab): ContestStatusFilter => {
    switch (tab) {
      case ContestTab.ACTIVE:
        return ContestStatusFilter.ACTIVE;
      case ContestTab.FINAL:
        return ContestStatusFilter.FINALIZED;
      default:
        return ContestStatusFilter.ACTIVE;
    }
  };

  const getPageTitle = (tab: ContestTab): string => {
    switch (tab) {
      case ContestTab.ACTIVE:
        return 'Entries Active';
      case ContestTab.FINAL:
        return 'Entries Final';
      default:
        return 'Entries Invites';
    }
  };

  useEffect(() => {
    reportPageView(router.pathname, getPageTitle(currentTab));
  }, [currentTab]);

  const isActionRequired =
    router.query[URLQueryKeys.CONTEST_STATUS] === ContestStatusFilter.ACTION_REQUIRED;

  const {
    keepEliminatedActive,
    hideEliminated,
    isToggleVisible,
    showEliminated,
    onShowEliminatedChange,
  } = useShowEliminatedContests({
    isActiveTab: currentTab === ContestTab.ACTIVE,
    isFinalTab: currentTab === ContestTab.FINAL,
    isActionRequired,
    isWebView: isWebView(),
  });

  const contestsData = useEntries({
    status: currentTab !== ContestTab.INVITES ? getContestStatusFilter(currentTab) : undefined,
    enabled: currentTab !== ContestTab.INVITES,
    keepEliminatedActive,
    hideEliminated,
  });

  const invitesData = useContestInvites({
    status: ContestInvitesStatusFilter.PENDING,
    enabled: currentTab === ContestTab.INVITES,
  });

  return {
    currentTab,
    contestsData,
    invitesData,
    isInvitesTab: currentTab === ContestTab.INVITES,
    contestStatusFilter: contestsData?.contestStatusFilter,
    contests: contestsData?.contests,
    isToggleVisible,
    showEliminated,
    onShowEliminatedChange,
  };
};

export default useEntriesPageData;
